import { DOC_KINDS, DOC_STATUSES, WAREHOUSE_KINDS } from '@epc/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Plus, Printer } from 'lucide-react';
import { useState } from 'react';
import { HistoryButton } from '../../components/History';
import { useCrumbs } from '../../layout/crumbs';
import { date } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, Spinner } from '../../ui/feedback';
import { KeyValue, Page, PageHeader, Panel } from '../../ui/layout';
import { DocumentDialog, WarehouseForm } from './forms';
import {
  DOC_TONE,
  documentsQuery,
  inventoryLastQuery,
  stockQuery,
  toolsQuery,
  warehousesQuery,
} from './queries';
import { StockTable } from './StockTable';
import { StartInventoryDialog } from './tabs2';

/** Карточка склада: реквизиты, остатки, документы (СК-01) */
export function WarehouseCardPage() {
  const { warehouseId } = useParams({ strict: false }) as { warehouseId: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const all = useQuery(warehousesQuery);
  const w = all.data?.find((x) => x.id === warehouseId);
  const stock = useQuery({ ...stockQuery({ warehouseId }), enabled: Boolean(w) });
  const docs = useQuery({ ...documentsQuery({ warehouseId }), enabled: Boolean(w) });
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [counting, setCounting] = useState(false);
  const last = useQuery(inventoryLastQuery);
  const tools = useQuery({ ...toolsQuery({ warehouseId }), enabled: Boolean(w) });
  const lastInv = last.data?.find((x) => x.warehouseId === warehouseId);
  useCrumbs(
    w
      ? [{ label: 'Склады', to: '/warehouse?tab=warehouses' }, { label: w.name }]
      : all.data
        ? [{ label: 'Не найдено' }]
        : null,
    all.error,
  );
  if (all.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (all.isError || !w)
    return (
      <Page>
        <EmptyState title="Склад не найден">Склад закрыт, удалён или вам не открыт.</EmptyState>
      </Page>
    );
  const rows = stock.data ?? [];
  return (
    <Page>
      <PageHeader
        title={w.name}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            {WAREHOUSE_KINDS[w.kind]}
            {w.projectCode ? <span>· {w.projectCode}</span> : null}
            {!w.isActive ? <Badge tone="neutral">закрыт</Badge> : null}
            {lastInv && w.kind !== 'transit' ? (
              <Badge tone={lastInv.overdue ? 'warning' : 'success'}>
                {lastInv.lastApprovedAt
                  ? `инвентаризация ${date(lastInv.lastApprovedAt)}`
                  : 'инвентаризации не было'}
              </Badge>
            ) : null}
            {tools.data?.length ? <Badge tone="neutral">инструмент: {tools.data.length}</Badge> : null}
          </span>
        }
        actions={
          <>
            {w.canOperate && w.kind !== 'transit' && w.isActive ? (
              <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                Документ
              </Button>
            ) : null}
            {w.canOperate && w.kind !== 'transit' && w.isActive ? (
              <Button onClick={() => setCounting(true)}>Инвентаризация</Button>
            ) : null}
            {w.canOperate && w.kind !== 'transit' ? (
              <Button onClick={() => setEditing(true)}>Править</Button>
            ) : null}
            {w.kind !== 'transit' ? (
              <a
                href={`/api/v1/warehouse/warehouses/${w.id}/balance.html`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-10 items-center gap-2 rounded-md border border-line-strong bg-surface px-4 text-[14px] font-medium text-ink hover:underline"
              >
                <Printer className="size-4" aria-hidden />
                Ведомость З-7
              </a>
            ) : null}
            <HistoryButton table="warehouses" rowId={w.id} title={w.name} />
          </>
        }
      />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-6">
          <Panel title="Остатки" meta={rows.length ? `${rows.length} поз.` : undefined} bodyClassName="p-0">
            {rows.length ? (
              <StockTable rows={rows} hide="warehouse" />
            ) : (
              <p className="p-5 text-[14px] text-ink-2">На складе пока пусто.</p>
            )}
          </Panel>
          <Panel title="Документы" bodyClassName="p-0">
            {docs.data?.length ? (
              <ul className="divide-y divide-line">
                {docs.data.map((d) => (
                  <li
                    key={d.id}
                    className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-5 py-2.5 text-[14px]"
                  >
                    <div>
                      <Link to={`/warehouse/documents/${d.id}`} className="font-medium hover:underline">
                        {d.number}
                      </Link>
                      <span className="ml-2 text-ink-2">
                        {DOC_KINDS[d.kind].title} · {date(d.docDate)}
                        {d.toWarehouseName ? ` → ${d.toWarehouseName}` : ''}
                      </span>
                    </div>
                    <Badge tone={DOC_TONE[d.status]}>{DOC_STATUSES[d.status]}</Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="p-5 text-[14px] text-ink-2">Документов по складу нет.</p>
            )}
          </Panel>
        </div>
        <Panel title="Склад">
          <KeyValue
            items={[
              { label: 'Вид', value: WAREHOUSE_KINDS[w.kind] },
              ...(w.projectCode ? [{ label: 'Проект', value: w.projectCode }] : []),
              ...(w.objectName ? [{ label: 'Объект', value: w.objectName }] : []),
              ...(w.counterpartyName ? [{ label: 'Подрядчик', value: w.counterpartyName }] : []),
              { label: 'Адрес', value: w.address ?? '—' },
              { label: 'МОЛ', value: w.molName ?? '—' },
              { label: 'Кладовщики', value: w.storekeeperNames.length ? w.storekeeperNames.join(', ') : '—' },
              { label: 'Код в 1С', value: w.code1c ?? '—' },
              ...(w.note ? [{ label: 'Примечание', value: w.note }] : []),
            ]}
          />
        </Panel>
      </div>
      {editing ? (
        <Dialog open onOpenChange={(v) => !v && setEditing(false)} title={w.name} width="lg">
          <WarehouseForm
            initial={w}
            onCancel={() => setEditing(false)}
            onSaved={() => {
              qc.invalidateQueries({ queryKey: ['wh'] });
              setEditing(false);
            }}
          />
        </Dialog>
      ) : null}
      {counting ? (
        <StartInventoryDialog
          presetWarehouseId={w.id}
          onClose={() => setCounting(false)}
          onDone={(d) => navigate({ to: `/warehouse/inventories/${d.id}` })}
        />
      ) : null}
      {creating ? (
        <DocumentDialog
          open
          onOpenChange={(v) => !v && setCreating(false)}
          presetWarehouseId={w.id}
          onSaved={(d) => navigate({ to: `/warehouse/documents/${d.id}` })}
        />
      ) : null}
    </Page>
  );
}
